import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Typography, Box } from "@mui/material";
import styled from "styled-components";
import DOMPurify from "dompurify";
import { ArrowForward, CloseIcon } from "../../shared/icons";
import { screenSizes } from "../../styles/theme";

const ReadMoreBtn = styled.button`
  border: none;
  outline: none;
  background: transparent !important;
  color: black !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  margin: 0 !important;
  padding: 0 !important;
  border-bottom: 1px solid black !important;
  border-radius: 0 !important;
  padding-bottom: 3px !important;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  &:hover {
    color: #ff9d59 !important;
    border-bottom: 1px solid #ff9d59 !important;
  }
  &:hover svg {
    filter: invert(68%) sepia(64%) saturate(370%) hue-rotate(344deg);
  }
  @media (max-width: ${screenSizes.L}px) {
      width: auto !important;
}

`;

// Function to sanitize HTML and ensure links are clickable
const sanitizeAndConvertLinks = (htmlContent: string): string => {
  // Use DOMPurify to sanitize HTML input
  const sanitizedHTML = DOMPurify.sanitize(htmlContent);
  const withoutSpans = sanitizedHTML.replace(/<\/?span[^>]*>/g, "");

  // Replace URLs in the sanitized HTML with clickable links
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return withoutSpans.replace(
    urlRegex,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
};

const TruncatedContent: React.FC<{ content: string }> = ({ content }) => {
  const [openModal, setOpenModal] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);

  const checkIfTruncated = useCallback(() => {
    if (textRef.current) {
      const lineHeight = 24;
      const maxHeight = lineHeight * 3;
      const isContentTruncated = textRef.current.scrollHeight > maxHeight;
      setIsTruncated(isContentTruncated);
    }
  }, []);

  useEffect(() => {
    checkIfTruncated();
  }, [content, checkIfTruncated]);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        overflowWrap: "anywhere",
      }}
    >
      <div
        ref={textRef}
        style={{
          fontSize: "16px",
          fontWeight: "600 !important",
          color: "#091c00",
          lineHeight: "24px",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          marginBottom: '1rem'
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeAndConvertLinks(content) }}
      />
      {isTruncated && (
        <ReadMoreBtn onClick={handleOpen}>
          Read more <ArrowForward style={{ height: "14px", width: "14px" }} />
        </ReadMoreBtn>
      )}

      <Modal open={openModal} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            padding: "30px",
            borderRadius: "0",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            maxWidth: 600,
            width: "80%",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "10px",
              cursor: "pointer",
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </Box>
          <Typography
            fontSize={"15px"}
            color="rgb(9, 28, 0)"
            dangerouslySetInnerHTML={{
              __html: sanitizeAndConvertLinks(content),
            }}
          />
        </Box>
      </Modal>
    </div>
  );
};

export default TruncatedContent;
